import React, { useState, useMemo, useEffect } from "react";
import { BreadcrumbComponent } from "../../../common/components";
import { Box } from "@mui/material";
import "./create-lead.scss";
import CardComponent from "../../../common/components/card-component/card-component";
import { Formik, Form } from "formik";
import TextInputComponent from "../../../common/components/text-input-component/text-input-component";
import PhoneNumberComponent from "../../../common/components/phone-number-input-component/phone-number-input-component";
import SelectInputComponent from "../../../common/components/select-input-component/select-input-component";
import PersonalInfoIcon from "../../assets/icons/personal-info.svg";
import UploadImage from "../../../common/components/upload-image/upload-image";
import DescriptionInputComponent from "../../../common/components/description-input/DescriptionInputComponent";
import RevenueInputComponent from "../../../common/components/revenue-input/RevenueInputComponent";
import { leadCreate } from "../../../api/lead";
import AlertComponent from "../../../common/components/alert-component/alert-component";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  LEAD_STATUS_OPTIONS,
  LEAD_SOURCE_OPTIONS,
  INTERESTED_PRODUCT_OPTIONS,
  RATING_OPTIONS,
} from "../../../constants/leadDropdown";
import leadInfoIcon from "../../../assets/icons/leadInfo.svg";
import { routes } from "../../../constants/routes";
import leadDetailsIcon from "../../../assets/icons/leadDetails.svg";
import addressIcon from "../../../assets/icons/address.svg";
import descriptionIcon from "../../../assets/icons/description.svg";
import { useSetLoading } from "../../../common/hooks/appearance";
import { initialValues, validationSchema } from "./create-lead-helper";
import OwnerIcon from "../../../assets/icons/owner.svg";
import CountryCodeWithPhoneNumber from "../../../common/components/phone-number-input-component/country-code-phone-numner-input";
import { useLeadOwnerOptions } from "../../../utils/getLeadOwnerOptions";

const breadcrumbLink = [
  { label: "Lead Management", link: routes.LEADLIST },
  { label: "Leads", link: routes.LEADLIST},
  { label: "Create Leads", link: "#", handleClick: () => {} },
];

const CreateLead: React.FC = () => {
  const [selectedInterestedProduct, setInterestedProduct] = useState([]);
  const LEAD_OWNER_OPTIONS = useLeadOwnerOptions();
    const [selectedLeadOwner, setSelectedLeadOwner] = useState(
      ""
    );
    useEffect(() => {
      if (LEAD_OWNER_OPTIONS?.length)setSelectedLeadOwner(LEAD_OWNER_OPTIONS[0].value)
    }, [LEAD_OWNER_OPTIONS])
  const [selectedLeadSource, setSelectedLeadSource] = useState("Manual");
  const [selectedLeadStatus, setSelectedLeadStatus] = useState("New Lead");
  const [selectedRating, setSelectedRating] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [isNavigate, setIsNavigate] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isPreviewImage, setPreviewImage] = useState(false);
  const handleAlertClose = () => {
    setAlertOpen(false);
  };
  const navigate = useNavigate();
  const handleCancel = async (values: any) => {
    navigate(routes.LEADLIST);
  };

  const handleSave = async (values: any, resetForm: any) => {
    setLoading(true);
    try {
      const formData = new FormData();

      const leadDetails = {
        company: values.company,
        title: values.title,
        firstName: values.firstName,
        lastName: values.lastName,
        primaryEmail: values.email,
        secondaryEmail: values.secondaryEmail || null,
        primaryPhoneNumber: values?.primaryPhoneNumber || null,
        secondaryPhoneNumber: values?.secondaryPhoneNumber || null,
        website: values.website || null,
        leadSource: selectedLeadSource || null,
        leadStatus: selectedLeadStatus || null,
        faxNumber: values.fax || null,
        industry: values.industry || null,
        numberOfEmployees: values.numEmployees
          ? Number(values.numEmployees)
          : null,
        annualRevenue: values.annualRevenue || null,
        interestedProducts:
          Array.isArray(selectedInterestedProduct) &&
          selectedInterestedProduct.length > 0
            ? selectedInterestedProduct.join(", ")
            : null,
        rating: selectedRating || null,
        socialLeadId: values.socialLeadId || null,
        description: values.description || null,
      };

      Object.entries(leadDetails).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      ["streetAddress", "city", "state", "country", "zipcode"].forEach(
        (field) => {
          if (values[field]) {
            formData.append(
              `address[${field.replace("Address", "")}]`,
              values[field]
            );
          }
        }
      );
      if (values.imageUrl instanceof File) {
        formData.append("leadImg", values.imageUrl);
      }

      const response = await leadCreate(formData);

      if (response.data.success) {
        setLoading(false);

        if (isNavigate) {
          navigate(routes.LEADLIST, {
            state: { message: "Lead created successfully", type: "success" },
          });
        } else {
          setAlertMessage("Lead saved successfully!");
          setAlertType("success");
          setAlertOpen(true);
          resetForm();
          setSelectedLeadOwner(LEAD_OWNER_OPTIONS[0].value);
          setSelectedLeadSource("Manual");
          setSelectedLeadStatus("New Lead");
          setSelectedRating("");
          setInterestedProduct([]);
          setPreviewImage(false)
        }
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(error?.response?.data?.message ||  "An error occurred while saving the lead.");
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const [selectOpen, setSelectOpen] = useState(false);

  const handleOpen = () => {
    setSelectOpen(true);
  };
  const handleBlur = () => {
    setSelectOpen(false);
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="leadcreate-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent
            breadcrumbLink={breadcrumbLink}
            showBackArrow={true}
          />
        </div>
        <div className="lead-create-filter-wrapper">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema(false)}
            onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              validateField,
              errors,
              touched,
              setFieldTouched,
            }) => (
              <Form>
                <div className="lead-form-wrapper">
                  <CardComponent
                    title="Lead Details"
                    icon={leadDetailsIcon}
                    isMandatory={true}
                  >
                    <div className="form-grid">
                      <div className="photo-container">
                        <UploadImage
                          label="Upload Photo"
                          text="Photo"
                          value={values?.imageUrl}
                          onChange={(file: any) => {
                            setFieldValue("imageUrl", file);
                            validateField("imageUrl");
                            setPreviewImage(true)
                          }}
                          onClick={() => {
                            setFieldTouched("imageUrl", true);
                          }}
                          showCloseButton
                          handleClose={() => {
                            setFieldValue("imageUrl", null);
                          }}
                          isPreviewImage={isPreviewImage}
                        />
                        <div>
                          {touched.imageUrl && errors.imageUrl && (
                            <div className="error-message">
                              {errors.imageUrl}
                            </div>
                          )}
                        </div>
                      </div>
                      <TextInputComponent
                        type="text"
                        fieldName="title"
                        label="Title"
                        placeholder="Enter title"
                        optional={false}
                        disabled={false}
                        value={values.title}
                        onChange={(e: any) =>
                          setFieldValue("title", e?.target?.value)
                        }
                        maxlength="50"
                      />
                      <TextInputComponent
                        type="text"
                        fieldName="firstName"
                        label="First Name"
                        placeholder="Enter first name"
                        optional={true}
                        disabled={false}
                        value={values.firstName}
                        onChange={(e: any) =>
                          setFieldValue("firstName", e?.target?.value)
                        }
                        isRequired={true}
                        isName={true}
                        minlength="3"
                        maxlength="50"
                      />
                      <TextInputComponent
                        type="text"
                        fieldName="lastName"
                        label="Last Name"
                        placeholder="Enter last name"
                        optional={true}
                        disabled={false}
                        value={values.lastName}
                        onChange={(e: any) =>
                          setFieldValue("lastName", e?.target?.value)
                        }
                        isRequired={true}
                        isName={true}
                        minlength="3"
                        maxlength="50"
                      />
                      <TextInputComponent
                        type="text"
                        fieldName="company"
                        label="Company"
                        placeholder="Enter company"
                        optional={true}
                        disabled={false}
                        value={values.company}
                        onChange={(e: any) =>
                          setFieldValue("company", e?.target?.value)
                        }
                        isRequired={true}
                        allowSpecialCharacters={true}
                        maxlength="150"
                      />
                      <SelectInputComponent
                        fieldName="leadOwner"
                        label="Lead Owner"
                        options={LEAD_OWNER_OPTIONS}
                        onChange={setSelectedLeadOwner}
                        value={selectedLeadOwner}
                        placeholder="Enter lead owner"
                        icon={OwnerIcon}
                        selectOpen={selectOpen}
                        handleOpen={handleOpen}
                        handleBlur={handleBlur}
                      />
                      <TextInputComponent
                        type="email"
                        fieldName="email"
                        label="Email"
                        placeholder="Enter email"
                        optional={true}
                        disabled={false}
                        value={values.email}
                        onChange={(e: any) =>
                          setFieldValue("email", e?.target?.value)
                        }
                        isRequired={true}
                      />
                      <TextInputComponent
                        type="email"
                        fieldName="secondaryEmail"
                        label="Secondary Email"
                        placeholder="Enter Secondary email"
                        optional={false}
                        disabled={false}
                        value={values.secondaryEmail}
                        onChange={(e: any) =>
                          setFieldValue("secondaryEmail", e?.target?.value)
                        }
                      />
                      <div className="form-group">
                        <CountryCodeWithPhoneNumber
                          fieldName="primaryPhoneNumber"
                          label="Primary Phone Number"
                          placeholder="Enter primary number"
                          onChange={(
                            value: string,
                            country: any,
                            one: any,
                            two: any
                          ) => {
                            const countryCode = country?.dialCode;

                            if (!countryCode) {
                              console.warn(
                                "Missing dialCode in country object"
                              );
                              return;
                            }

                            setFieldValue("primaryPhoneNumber", two);
                          }}
                          onBlur={() => {
                            setFieldTouched("primaryPhoneNumber", true);
                          }}
                          value={values?.primaryPhoneNumber || "+1"}
                          defaultCountry="us"
                          optional={true}
                          // countryCodeEditable
                        />
                        {touched.primaryPhoneNumber &&
                          touched?.primaryPhoneNumber && (
                            <div className="error-message">
                              {errors.primaryPhoneNumber}
                            </div>
                          )}
                      </div>
                      <div className="form-group">
                        <CountryCodeWithPhoneNumber
                          fieldName="secondaryPhoneNumber"
                          label="Secondary Phone Number"
                          placeholder="Enter Secondary number"
                          onChange={(
                            value: string,
                            country: any,
                            one: any,
                            two: any
                          ) => {
                            const countryCode = country?.dialCode;

                            if (!countryCode) {
                              console.warn(
                                "Missing dialCode in country object"
                              );
                              return;
                            }

                            setFieldValue("secondaryPhoneNumber", two);
                          }}
                          onBlur={() => {
                            setFieldTouched("secondaryPhoneNumber", true);
                          }}
                          value={values?.secondaryPhoneNumber || "+1"}
                          defaultCountry="us"
                          optional={true}
                          // countryCodeEditable
                        />{" "}
                        {touched.secondaryPhoneNumber &&
                          touched?.secondaryPhoneNumber && (
                            <div className="error-message">
                              {errors.secondaryPhoneNumber}
                            </div>
                          )}
                      </div>
                      <div className="form-group">
                        <CountryCodeWithPhoneNumber
                          fieldName="fax"
                          label="Fax"
                          placeholder="Enter fax"
                          onChange={(
                            value: string,
                            country: any,
                            one: any,
                            two: any
                          ) => {
                            const countryCode = country?.dialCode;

                            if (!countryCode) {
                              console.warn(
                                "Missing dialCode in country object"
                              );
                              return;
                            }

                            setFieldValue("fax", two);
                          }}
                          onBlur={() => {
                            setFieldTouched("fax", true);
                          }}
                          value={values?.fax || "+1"}
                          defaultCountry="us"
                          optional={true}
                          // countryCodeEditable
                        />{" "}
                        {touched.fax &&
                          touched?.fax && (
                            <div className="error-message">
                              {errors.fax}
                            </div>
                          )}
                      </div>
                      <TextInputComponent
                        type="Website"
                        fieldName="website"
                        label="Website"
                        placeholder="Enter website"
                        optional={false}
                        disabled={false}
                        value={values.website}
                        onChange={(e: any) =>
                          setFieldValue("website", e?.target?.value)
                        }
                        maxlength="150"
                      />
                      <TextInputComponent
                        type="text"
                        fieldName="numEmployees"
                        label="Number of Employees"
                        placeholder="Enter no. of Employees"
                        optional={false}
                        disabled={false}
                        value={values.numEmployees}
                        onChange={(e: any) =>
                          setFieldValue("numEmployees", e?.target?.value)
                        }
                        isNumber={true}
                        maxlength="7"
                      />
                      <SelectInputComponent
                        fieldName="leadSource"
                        label="Lead Source"
                        options={LEAD_SOURCE_OPTIONS}
                        onChange={setSelectedLeadSource}
                        value={selectedLeadSource || "None"}
                      />
                      <SelectInputComponent
                        fieldName="leadStatus"
                        label="Lead Status"
                        options={LEAD_STATUS_OPTIONS}
                        onChange={setSelectedLeadStatus}
                        value={selectedLeadStatus || "None"}
                      />
                      <TextInputComponent
                        type="text"
                        label="Industry"
                        fieldName="industry"
                        value={values.industry}
                        placeholder="Enter industry"
                        optional={false}
                        disabled={false}
                        onChange={(e: any) =>
                          setFieldValue("industry", e?.target?.value)
                        }
                        maxlength="50"
                      />
                      <RevenueInputComponent
                        fieldName="annualRevenue"
                        label="Lead Value ($)"
                        placeholder="Enter revenue amount"
                        optional={false}
                        disabled={false}
                        value={values.annualRevenue}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setFieldValue("annualRevenue", input);
                          }
                        }}
                        logo={leadInfoIcon}
                      />
                      <TextInputComponent
                        type="text"
                        fieldName="socialLeadId"
                        label="Social Lead ID"
                        placeholder="Enter social lead id"
                        optional={false}
                        disabled={false}
                        value={values.socialLeadId}
                        onChange={(e: any) =>
                          setFieldValue("socialLeadId", e?.target?.value)
                        }
                        maxlength="70"
                      />
                      <SelectInputComponent
                        fieldName="interestedProduct"
                        label="Interested Product"
                        options={INTERESTED_PRODUCT_OPTIONS}
                        onChange={setInterestedProduct}
                        value={selectedInterestedProduct}
                        placeholder=" Select interested product"
                        multiple={true}
                      />
                      <SelectInputComponent
                        fieldName="rating"
                        label="Confidence Score"
                        options={RATING_OPTIONS}
                        onChange={setSelectedRating}
                        value={selectedRating} // This will bind the selected value
                        placeholder="Select confidence score"
                        colorProp={true}
                        closeIcon
                        onClear={() => setSelectedRating("")}
                      />
                    </div>
                  </CardComponent>
                  <div className="card-container">
                    <CardComponent
                      title="Address Information"
                      icon={addressIcon}
                    >
                      <div className="form-grid-two">
                        <TextInputComponent
                          fieldName="streetAddress"
                          label="Street"
                          placeholder="Enter street"
                          optional={false}
                          value={values.streetAddress}
                          type="text"
                          onChange={(e: any) =>
                            setFieldValue("streetAddress", e?.target?.value)
                          }
                          maxlength="100"
                        />
                        <TextInputComponent
                          fieldName="state"
                          label="State"
                          placeholder="Enter state"
                          optional={false}
                          value={values.state}
                          onChange={(e: any) =>
                            setFieldValue("state", e?.target?.value)
                          }
                          type="text"
                          maxlength="100"
                        />
                        <TextInputComponent
                          fieldName="country"
                          label="Country"
                          placeholder="Enter country"
                          optional={false}
                          value={values.country}
                          onChange={(e: any) =>
                            setFieldValue("country", e?.target?.value)
                          }
                          type="text"
                          maxlength="100"
                        />
                        <TextInputComponent
                          fieldName="city"
                          label="City"
                          placeholder="Enter city"
                          optional={false}
                          value={values.city}
                          onChange={(e: any) =>
                            setFieldValue("city", e?.target?.value)
                          }
                          type="text"
                          maxlength="100"
                        />
                        <TextInputComponent
                          fieldName="zipcode"
                          label="Zipcode"
                          placeholder="Enter zipcode"
                          optional={false}
                          value={values.zipcode}
                          onChange={(e: any) =>
                            setFieldValue("zipcode", e?.target?.value)
                          }
                          type="text"
                          maxlength="5"
                        />
                      </div>
                    </CardComponent>

                    <CardComponent title="Description" icon={descriptionIcon}>
                      <div className="form-grid-three">
                        <DescriptionInputComponent
                          fieldName="description"
                          label="Company Description"
                          placeholder="Add description"
                          optional={false}
                          disabled={false}
                          value={values.description}
                          onChange={(e: any) =>
                            setFieldValue("description", e?.target?.value)
                          }
                          maxlength={150}
                        />
                      </div>
                    </CardComponent>
                  </div>
                </div>
                <AlertComponent
                  message={alertMessage}
                  type={alertType}
                  open={alertOpen}
                  onClose={handleAlertClose}
                />
                <Box className="footer">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      handleSubmit();
                      setIsNavigate(false);
                    }}
                  >
                    Save & New
                  </button>
                  <button
                    type="button"
                    className="cancel-button next-button-wrapper"
                    onClick={() => {
                      handleSubmit();
                      setIsNavigate(true);
                    }}
                  >
                    Save
                  </button>
                </Box>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default CreateLead;
